import type { TFunction } from 'i18next';

import { formatMoney } from '../../lib/currency';

export type PlanKey = 'lite' | 'litePlus' | 'liteAutopilot' | 'professional' | 'premium' | 'enterprise';

export const LITE_PLAN_KEYS = ['lite', 'litePlus', 'liteAutopilot'] as const satisfies readonly PlanKey[];
export type LitePlanKey = (typeof LITE_PLAN_KEYS)[number];

export const isLitePlan = (key: PlanKey): key is LitePlanKey =>
  (LITE_PLAN_KEYS as readonly PlanKey[]).includes(key);
export const GEOFLEET_PLAN_KEYS = ['professional', 'premium', 'enterprise'] as const satisfies readonly PlanKey[];

/**
 * List prices in USD — what we bill, and the base the currency switcher
 * converts from. `null` = quoted, not listed (Enterprise).
 */
export const PLAN_PRICE_USD: Record<PlanKey, number | null> = {
  lite: 49,
  litePlus: 99,
  liteAutopilot: 179,
  professional: 19,
  premium: 29,
  enterprise: null,
};

export const PLAN_INCLUDED_DRIVERS: Record<PlanKey, number | null> = {
  lite: 3,
  litePlus: 10,
  liteAutopilot: 25,
  professional: 10,
  premium: 10,
  enterprise: null,
};

/** Lite allowances are flat. Full Geofleet allowances scale with active drivers. */
export const PLAN_ORDERS_PER_DRIVER: Record<PlanKey, number | null> = {
  lite: null,
  litePlus: null,
  liteAutopilot: null,
  professional: 500,
  premium: 1000,
  enterprise: null,
};

export const LITE_INCLUDED_ORDERS: Record<LitePlanKey, number> = {
  lite: 500,
  litePlus: 1000,
  liteAutopilot: 2000,
};

/** Lite billing cycles. Allowances and overages stay monthly on every cycle. */
export const BILLING_CYCLES = ['monthly', 'quarterly', 'annual'] as const;
export type BillingCycle = (typeof BILLING_CYCLES)[number];

export const BILLING_CYCLE_MONTHS: Record<BillingCycle, number> = {
  monthly: 1,
  quarterly: 3,
  annual: 12,
};

/** Effective USD price per month for each Lite plan on each billing cycle. */
export const LITE_PRICE_BY_CYCLE: Record<LitePlanKey, Record<BillingCycle, number>> = {
  lite: { monthly: 49, quarterly: 44, annual: 39 },
  litePlus: { monthly: 99, quarterly: 89, annual: 79 },
  liteAutopilot: { monthly: 179, quarterly: 159, annual: 149 },
};

/** USD per extra driver per month, beyond the plan's included drivers. */
export const LITE_EXTRA_DRIVER_USD: Record<LitePlanKey, number> = {
  lite: 10,
  litePlus: 10,
  liteAutopilot: 8,
};

/** USD per extra order, beyond the plan's monthly order allowance. */
export const LITE_EXTRA_ORDER_USD: Record<LitePlanKey, number> = {
  lite: 0.08,
  litePlus: 0.06,
  liteAutopilot: 0.04,
};

/** Staff (dispatcher) seats. `null` = unlimited. */
export const LITE_STAFF_SEATS: Record<LitePlanKey, number | null> = {
  lite: 2,
  litePlus: 5,
  liteAutopilot: null,
};

/** Total charged per billing period, in USD. */
export const litePeriodTotalUsd = (plan: LitePlanKey, cycle: BillingCycle) =>
  LITE_PRICE_BY_CYCLE[plan][cycle] * BILLING_CYCLE_MONTHS[cycle];

/** Whole-percent saving against paying monthly. */
export const liteSavingPercent = (plan: LitePlanKey, cycle: BillingCycle) => {
  const monthly = LITE_PRICE_BY_CYCLE[plan].monthly;
  return Math.round((1 - LITE_PRICE_BY_CYCLE[plan][cycle] / monthly) * 100);
};

/** Largest saving any Lite plan gets on a cycle, for the toggle badge. */
export const liteMaxSavingPercent = (cycle: BillingCycle) =>
  Math.max(...LITE_PLAN_KEYS.map((plan) => liteSavingPercent(plan, cycle)));

/** Plan price in the chosen display currency: the USD price, converted for reference. */
export function getPlanPriceDisplay(planKey: PlanKey, t: TFunction, currency: string): string {
  const amountUsd = PLAN_PRICE_USD[planKey];
  if (amountUsd === null) return t(`pricing.plans.${planKey}.price`);
  return formatMoney(amountUsd, currency);
}

/** GeoFleet plan codes (geo-nexus platform.plans) behind each Lite plan. */
export const LITE_PLAN_CODES: Record<LitePlanKey, string> = {
  lite: 'LITE',
  litePlus: 'LITE_PLUS',
  liteAutopilot: 'LITE_AUTOPILOT',
};

/** GeoFleet billing cycle codes for each Lite billing cycle. */
export const BILLING_CYCLE_CODES: Record<BillingCycle, string> = {
  monthly: 'MONTHLY',
  quarterly: 'QUARTERLY',
  annual: 'YEARLY',
};

/** Lite sign-up link that carries the chosen plan and billing cycle, e.g. ?plan=LITE_PLUS&cycle=YEARLY. */
export const liteSignupUrl = (base: string, plan: LitePlanKey, cycle: BillingCycle) => {
  const url = new URL(base);
  url.searchParams.set('plan', LITE_PLAN_CODES[plan]);
  url.searchParams.set('cycle', BILLING_CYCLE_CODES[cycle]);
  return url.toString();
};
